/**
 * answer-icon.js
 */

import React from 'react';
import { Tooltip, Badge } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { parseISO } from 'date-fns';
import { Types } from 'utils/constants';
import { UserContext } from 'contexts/user/user-context';
import Icon from 'components/icon';
import IconReview from 'components/icon-review';


const useStyles = makeStyles(theme => ({
  label: {
    marginLeft: theme.spacing(1),
  },
  icon: {
    pointerEvents: 'none'
  },
  badge: {
    '& .MuiBadge-badge': {
      top: 3,
      padding: 3,
      minWidth: 'auto',
      height: 'auto',
      fontSize: theme.typography.sizes[0],
    }
  },
}));


export default function AnswerIcon(props) {
  const {
    answer,
    membership,
    label,
    showTooltip,
    dogList,
    forceIconName,
    forceIconClass,
    allowRejected
  } = props;
  const classes = useStyles();
  const { user } = React.useContext(UserContext);

  const dogName = dogList
    ? dogList.join(', ')
    : `K9 ${answer.dog.name.replace('K9', '')}`;
  const isFutureEvent = answer ? parseISO(answer.eventDate) > new Date() : false;
  const isUndecided = membership?.isOptional && membership?.isAttending === null;
  const isNotAttending = membership?.isOptional && membership?.isAttending === false;

  let iconName = '';
  let iconClass = '';
  let iconTitle = '';


  // override icon type from props
  if (forceIconName) {
    iconName = forceIconName;
    iconClass = forceIconClass;
  } 
  // undecided
  else if (isUndecided) {
    iconName = 'undecided';
    iconClass = 'icon-unknown';
  }
  // not-applicable
  else if (!answer.dog.appliesToExercise) {
    iconName = 'not-applicable';
    iconClass = isUndecided || isNotAttending ? 'icon-unknown' : 'icon-na';
  }
  // not attending
  else if (isNotAttending) {
    iconName = 'not-attending';
    iconClass = 'icon-unknown';
  }
  // upcoming
  else if (isFutureEvent) {
    iconName = 'upcoming';
    iconClass = isNotAttending ? 'icon-unknown' : 'icon-upcoming';
  }
  // rejected
  else if (allowRejected && answer.review?.status === Types.ReviewStatus.REJECTED) {
    iconName = 'rejected';
    iconClass = 'icon-error';
  }
  // outdated (handler role only)
  else if (answer.isOutdated && user.role.isHandler) {
    iconName = 'outdated';
    iconClass = 'icon-outdated';
  }
  // complete
  else if (answer.dateCompleted) {
    iconName = 'complete';
    iconClass = 'icon-complete';
  } 
  // incomplete
  else if (!answer.dateCompleted) {
    iconName = 'incomplete';
    iconClass = 'icon-incomplete';
  }


  // not-applicable
  if (iconName === 'not-applicable') {
    iconTitle = `Exercise not applicable for ${dogName}`;
  } 
  // undecided
  else if (iconName === 'undecided') {
    iconTitle = `Exercise attendance undecided for ${dogName}`;
  }
  // not-attending
  else if (iconName === 'not-attending') {
    iconTitle = `Exercise not attended by ${dogName}`;
  }
  // upcoming
  else if (iconName === 'upcoming') {
    iconTitle = `Exercise upcoming for ${dogName}`;
  }
  // rejected
  else if (iconName === 'rejected') {
    iconTitle = `Exercise record rejected for ${dogName}`;
  }
  // outdated (handler role only)
  else if (iconName === 'outdated' && user.role.isHandler) {
    iconTitle = `Exercise outdated for ${dogName}`;
  }
  // complete
  else if (iconName === 'complete') {
    iconTitle = `Exercise complete for ${dogName}`;
  } 
  // incomplete
  else if (iconName === 'incomplete') {
    iconTitle = `Exercise incomplete for ${dogName}`;
  }


  let icon = <Icon type={iconName} className={`${classes.icon} ${forceIconClass || iconClass}`} />;


  if (user.role.isSupervisor && answer.dateCompleted) {
    icon = <IconReview
      isComplete={true}
      status={answer.review?.status}
    />;
  
    let statusName = '';
    switch (answer.review?.status) {
      default: 
      case Types.ReviewStatus.NOT_REVIEWED:
        statusName = 'not reviewed';
        break;

      case Types.ReviewStatus.ACCEPTED:
        statusName = 'accepted';
        break;

      case Types.ReviewStatus.REJECTED:
        statusName = 'rejected';
        break;
    }
    iconTitle = `Exercise record ${statusName} for ${answer.dog.name}`;
  }


  const iconBase = <>
    {icon}
    {label && <span className={classes.label}>{label}</span>}
  </>;


  const iconWithBadge = dogList
    ? <Badge
      color="primary"
      className={classes.badge}
      badgeContent={dogList.length}
      children={iconBase}
    />
    : iconBase;


  const iconFull = showTooltip
    ? <Tooltip arrow placement='top' title={iconTitle}>
      <span style={{ display: 'flex' }}>{iconWithBadge}</span>
    </Tooltip>
    : iconWithBadge;


  return (
    <>
      {iconName &&
      <>
        {iconFull}
      </>}
    </>
  );
}